import { Field, Formik } from "formik";
import React from "react";
import { Alert, Form, FormGroup, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { ReduxState } from "../../../../../redux";
import {
  formikCheckInput,
  formikInput,
  formikSelectInput,
} from "../../../../../utils/utils";
import PopoverHelp from "../../../../Docuclipper/PopoverHelp";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import { QboSearchField } from "./QboSearch/QboSearch";
import { downloadSpreadsheetHelper } from "./utils";
import { groupBy } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const LS_KEY_QBO_OPTIONS = "docuclipper.prefs.qboOptions";

type QboFormValues = {
  bid: "" | { bid: string; name: string };
  accountId: string;
  bankId: string;
  accountType: "checking" | "creditCard";
  fromAccount: string;
  currency: string;
};

const DEFAULT_QBO_OPTIONS: QboFormValues & { separateFilesForAccounts: boolean } = {
  bid: "",
  accountId: "123456789",
  bankId: "123456789",
  accountType: "checking",
  fromAccount: "",
  currency: "USD",
  separateFilesForAccounts: true,
};

function getStickyQboOptions(): typeof DEFAULT_QBO_OPTIONS {
  try {
    const raw = localStorage.getItem(LS_KEY_QBO_OPTIONS);
    if (!raw) return { ...DEFAULT_QBO_OPTIONS };
    const parsed = JSON.parse(raw);
    const bid =
      parsed.bid &&
      typeof parsed.bid === "object" &&
      typeof parsed.bid.bid === "string" &&
      typeof parsed.bid.name === "string"
        ? parsed.bid
        : DEFAULT_QBO_OPTIONS.bid;
    return {
      bid,
      accountId:
        typeof parsed.accountId === "string" ? parsed.accountId : DEFAULT_QBO_OPTIONS.accountId,
      bankId:
        typeof parsed.bankId === "string" ? parsed.bankId : DEFAULT_QBO_OPTIONS.bankId,
      accountType:
        parsed.accountType === "creditCard" ? "creditCard" : DEFAULT_QBO_OPTIONS.accountType,
      fromAccount:
        typeof parsed.fromAccount === "string" ? parsed.fromAccount : DEFAULT_QBO_OPTIONS.fromAccount,
      currency:
        typeof parsed.currency === "string" ? parsed.currency : DEFAULT_QBO_OPTIONS.currency,
      separateFilesForAccounts:
        typeof parsed.separateFilesForAccounts === "boolean"
          ? parsed.separateFilesForAccounts
          : DEFAULT_QBO_OPTIONS.separateFilesForAccounts,
    };
  } catch {
    return { ...DEFAULT_QBO_OPTIONS };
  }
}

function saveStickyQboOptions(values: {
  bid: string | { bid: string; name: string };
  accountId: string;
  bankId: string;
  accountType: string;
  fromAccount: string;
  currency: string;
  separateFilesForAccounts?: boolean;
}) {
  try {
    localStorage.setItem(
      LS_KEY_QBO_OPTIONS,
      JSON.stringify({
        bid: values.bid,
        accountId: values.accountId,
        bankId: values.bankId,
        accountType: values.accountType,
        fromAccount: values.fromAccount,
        currency: values.currency,
        separateFilesForAccounts: values.separateFilesForAccounts,
      })
    );
  } catch (_) {}
}

const MAIN_CURRENCIES = [
  { code: "USD", name: "United States Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound Sterling" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "ZAR", name: "Rand" },
];

const OTHER_CURRENCIES = [
  { code: "AED", name: "UAE Dirham" },
  { code: "AFN", name: "Afghan Afghani" },
  { code: "ALL", name: "Albanian Lek" },
  { code: "AMD", name: "Armenian Dram" },
  { code: "ANG", name: "Netherlands Antillean Guilder (Curaçao)" },
  { code: "AOA", name: "Angolan Kwanza" },
  { code: "ARS", name: "Argentine Peso" },
  { code: "AWG", name: "Aruban Florin" },
  { code: "AZN", name: "Azerbaijani Manat" },
  { code: "BAM", name: "Bosnia-Herzegovina Convertible Mark" },
  { code: "BBD", name: "Barbadian Dollar" },
  { code: "BDT", name: "Bangladeshi Taka" },
  { code: "BGN", name: "Bulgarian Lev" },
  { code: "BHD", name: "Bahraini Dinar" },
  { code: "BIF", name: "Burundian Franc" },
  { code: "BMD", name: "Bermudan Dollar" },
  { code: "BND", name: "Brunei Dollar" },
  { code: "BOB", name: "Bolivian Boliviano" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "BSD", name: "Bahamian Dollar" },
  { code: "BTN", name: "Bhutanese Ngultrum" },
  { code: "BWP", name: "Botswanan Pula" },
  { code: "BYN", name: "Belarusian Ruble" },
  { code: "BZD", name: "Belize Dollar" },
  { code: "CDF", name: "Congolese Franc" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "CLP", name: "Chilean Peso" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "COP", name: "Colombian Peso" },
  { code: "CRC", name: "Costa Rican Colón" },
  { code: "CUP", name: "Cuban Peso" },
  { code: "CVE", name: "Cape Verdean Escudo" },
  { code: "CZK", name: "Czech Koruna" },
  { code: "DJF", name: "Djiboutian Franc" },
  { code: "DKK", name: "Danish Krone" },
  { code: "DOP", name: "Dominican Peso" },
  { code: "DZD", name: "Algerian Dinar" },
  { code: "EGP", name: "Egyptian Pound" },
  { code: "ERN", name: "Eritrean Nakfa" },
  { code: "ETB", name: "Ethiopian Birr" },
  { code: "FJD", name: "Fijian Dollar" },
  { code: "FKP", name: "Falkland Islands Pound" },
  { code: "GEL", name: "Georgian Lari" },
  { code: "GHS", name: "Ghanaian Cedi" },
  { code: "GIP", name: "Gibraltar Pound" },
  { code: "GMD", name: "Gambian Dalasi" },
  { code: "GNF", name: "Guinean Franc" },
  { code: "GTQ", name: "Guatemalan Quetzal" },
  { code: "GYD", name: "Guyanaese Dollar" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "HNL", name: "Honduran Lempira" },
  { code: "HRK", name: "Croatian Kuna" },
  { code: "HTG", name: "Haitian Gourde" },
  { code: "HUF", name: "Hungarian Forint" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "ILS", name: "Israeli New Shekel" },
  { code: "INR", name: "Indian Rupee" },
  { code: "IQD", name: "Iraqi Dinar" },
  { code: "IRR", name: "Iranian Rial" },
  { code: "ISK", name: "Icelandic Króna" },
  { code: "JMD", name: "Jamaican Dollar" },
  { code: "JOD", name: "Jordanian Dinar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "KES", name: "Kenyan Shilling" },
  { code: "KGS", name: "Kyrgystani Som" },
  { code: "KHR", name: "Cambodian Riel" },
  { code: "KMF", name: "Comorian Franc" },
  { code: "KPW", name: "North Korean Won" },
  { code: "KRW", name: "South Korean Won" },
  { code: "KWD", name: "Kuwaiti Dinar" },
  { code: "KYD", name: "Cayman Islands Dollar" },
  { code: "KZT", name: "Kazakhstani Tenge" },
  { code: "LAK", name: "Laotian Kip" },
  { code: "LBP", name: "Lebanese Pound" },
  { code: "LKR", name: "Sri Lankan Rupee" },
  { code: "LRD", name: "Liberian Dollar" },
  { code: "LSL", name: "Lesotho Loti" },
  { code: "LYD", name: "Libyan Dinar" },
  { code: "MAD", name: "Moroccan Dirham" },
  { code: "MDL", name: "Moldovan Leu" },
  { code: "MGA", name: "Malagasy Ariary" },
  { code: "MKD", name: "Macedonian Denar" },
  { code: "MMK", name: "Myanma Kyat" },
  { code: "MNT", name: "Mongolian Tugrik" },
  { code: "MOP", name: "Macanese Pataca" },
  { code: "MRU", name: "Mauritanian Ouguiya" },
  { code: "MUR", name: "Mauritian Rupee" },
  { code: "MVR", name: "Maldivian Rufiyaa" },
  { code: "MWK", name: "Malawian Kwacha" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "MZN", name: "Mozambican Metical" },
  { code: "NAD", name: "Namibian Dollar" },
  { code: "NGN", name: "Nigerian Naira" },
  { code: "NIO", name: "Nicaraguan Córdoba" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "NPR", name: "Nepalese Rupee" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "OMR", name: "Omani Rial" },
  { code: "PAB", name: "Panamanian Balboa" },
  { code: "PEN", name: "Peruvian Sol" },
  { code: "PGK", name: "Papua New Guinean Kina" },
  { code: "PHP", name: "Philippine Peso" },
  { code: "PKR", name: "Pakistani Rupee" },
  { code: "PLN", name: "Polish Zloty" },
  { code: "PYG", name: "Paraguayan Guarani" },
  { code: "QAR", name: "Qatari Rial" },
  { code: "RON", name: "Romanian Leu" },
  { code: "RSD", name: "Serbian Dinar" },
  { code: "RUB", name: "Russian Ruble" },
  { code: "RWF", name: "Rwandan Franc" },
  { code: "SAR", name: "Saudi Riyal" },
  { code: "SBD", name: "Solomon Islands Dollar" },
  { code: "SCR", name: "Seychellois Rupee" },
  { code: "SDG", name: "Sudanese Pound" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "SHP", name: "Saint Helena Pound" },
  { code: "SLL", name: "Sierra Leonean Leone" },
  { code: "SOS", name: "Somali Shilling" },
  { code: "SRD", name: "Surinamese Dollar" },
  { code: "SSP", name: "South Sudanese Pound" },
  { code: "STN", name: "São Tomé and Príncipe Dobra" },
  { code: "SYP", name: "Syrian Pound" },
  { code: "SZL", name: "Swazi Lilangeni" },
  { code: "THB", name: "Thai Baht" },
  { code: "TJS", name: "Tajikistani Somoni" },
  { code: "TMT", name: "Turkmenistani Manat" },
  { code: "TND", name: "Tunisian Dinar" },
  { code: "TOP", name: "Tongan Paʻanga" },
  { code: "TRY", name: "Turkish Lira" },
  { code: "TTD", name: "Trinidad and Tobago Dollar" },
  { code: "TWD", name: "New Taiwan Dollar" },
  { code: "TZS", name: "Tanzanian Shilling" },
  { code: "UAH", name: "Ukrainian Hryvnia" },
  { code: "UGX", name: "Ugandan Shilling" },
  { code: "UYU", name: "Uruguayan Peso" },
  { code: "UZS", name: "Uzbekistan Som" },
  { code: "VES", name: "Venezuelan Bolívar" },
  { code: "VND", name: "Vietnamese Dong" },
  { code: "VUV", name: "Vanuatu Vatu" },
  { code: "WST", name: "Samoan Tala" },
  { code: "XAF", name: "Central African CFA Franc" },
  { code: "XCD", name: "East Caribbean Dollar" },
  { code: "XOF", name: "West African CFA Franc" },
  { code: "XPF", name: "CFP Franc" },
  { code: "YER", name: "Yemeni Rial" },
  { code: "ZMW", name: "Zambian Kwacha" },
  { code: "ZWL", name: "Zimbabwean Dollar" },
];

export const QboOptions: React.FC<{ format }> = ({ format }) => {
  const sticky = React.useMemo(() => getStickyQboOptions(), []);
  const [loading, setLoading] = React.useState(false);
  const [separateFilesForAccounts, setSeparateFilesForAccounts] =
    React.useState(sticky.separateFilesForAccounts);
  const { selectedExportType } = useSelector(
    (state: ReduxState) => state.DownloadOptions
  );
  useSelector((state: ReduxState) => state.TransactionManager);

  const { job } = useSelector((state: ReduxState) => state.JobData);

  const { downloadOptions } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { selectedDocumentIds, selectedFieldNames } = downloadOptions;
  // const allAccounts = Object.keys(groupBy(tls.tls || [], "account"));
  const { allAccounts } = useSelector((state: ReduxState) => state.Reconciler);

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const onExport = ({
    accountId,
    bankId,
    accountType,
    fromAccount,
    bid,
    bankName,
    currency,
  }) => {
    return downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "FieldsToExcel1",
      format,
      {
        accountId,
        bankId,
        accountType,
        fromAccount,
        getTotals: false,
        bid,
        bankName,
        currency,
        separateFilesForAccounts,
      },
      selectedDocumentIds,
      selectedFieldNames,
      [],
      [
        {
          field: "fieldName",
          operator: "equals",
          value: "bankMode",
        },
      ],
      "date",
      "asc"
    );
  };
  const onSubmit =
    () =>
    ({ accountId, bankId, accountType, fromAccount, bid, currency }) => {
      const { bid: bid2, name } = bid;
      setLoading(true);
      onExport({
        accountId,
        bankId,
        fromAccount,
        accountType,
        bid: bid2,
        bankName: name,
        currency,
      })
        .then(() => {
          setLoading(false);
          saveStickyQboOptions({
            bid,
            accountId,
            bankId,
            accountType,
            fromAccount,
            currency,
            separateFilesForAccounts,
          });
        })
        .catch(() => {
          setLoading(false);
          //
        });
    };

  const bidDef = ["qbo"].includes(format)
    ? Yup.string().required(
        `Bank is required. Select "Chase Web Download" if your bank is not listed.`
      )
    : Yup.string();

  const format2str = () => {
    switch (format) {
      case "qbo":
        return "QBO Web Connect";
      case "ofx":
        return "OFX";
      case "qfx":
        return "QFX";
      default:
        return "";
    }
  };
  return (
    <Formik<QboFormValues>
      initialValues={{
        bid: sticky.bid,
        accountId: sticky.accountId,
        bankId: sticky.bankId,
        accountType: sticky.accountType,
        fromAccount: sticky.fromAccount,
        currency: sticky.currency,
      }}
      validationSchema={Yup.object().shape({
        bid: bidDef,
        accountId: Yup.string()
          .min(4, "Account number must be at least 4 characters")
          .max(22, "Account number must be at most 22 characters")
          .required("Account number is required"),
        bankId: Yup.string().when("accountType", {
          is: "checking",
          then: (schema) => schema.required("Routing number is required"),
          otherwise: (schema) => schema,
        }),
        accountType: Yup.string().required(""),
        currency: Yup.string().required(""),
      })}
      onSubmit={onSubmit()}
    >
      {({ submitForm, values }) => (
        <>
          <FormGroup>
            {format === "qbo" && (
              <div className="mb-2">
                <div>
                  <Form.Label>
                    <strong>Bank</strong>
                  </Form.Label>
                </div>

                <Field inline={true} name="bid" component={QboSearchField} />
              </div>
            )}
            <div className="">
              <Form.Label>
                <strong>Account type</strong>
              </Form.Label>
            </div>

            <Field
              inline={true}
              name="accountType"
              type="radio"
              value="checking"
              checked={values.accountType === "checking"}
              component={formikCheckInput}
              label="Bank"
            />

            <Field
              inline={true}
              name="accountType"
              type="radio"
              value="creditCard"
              checked={values.accountType === "creditCard"}
              component={formikCheckInput}
              label="Credit Card"
            />
          </FormGroup>
          {true && (
            <>
              {values.accountType === "checking" && (
                <FormGroup>
                  <Form.Label>
                    <strong>Routing number</strong>
                  </Form.Label>
                  <PopoverHelp header="" placement="right" id="bankIdHelp">
                    Optional for QuickBooks Online. Recommended for QuickBooks
                    Desktop.
                  </PopoverHelp>
                  <Field
                    name="bankId"
                    type="text"
                    component={formikInput}
                  ></Field>
                </FormGroup>
              )}
              <FormGroup>
                <Form.Label>
                  <strong>Account number</strong>
                </Form.Label>
                <PopoverHelp header="" placement="right" id="accountIdHelp">
                  Optional for QuickBooks Online. Recommended for QuickBooks
                  Desktop.
                </PopoverHelp>
                <Field
                  name="accountId"
                  type="text"
                  component={formikInput}
                ></Field>
              </FormGroup>
              <FormGroup>
                <Form.Label>
                  <strong>Currency</strong>
                </Form.Label>
                <Field
                  name="currency"
                  type="select"
                  component={formikSelectInput}
                >
                  <optgroup label="Main currencies">
                    {MAIN_CURRENCIES.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {code} - {name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Other currencies">
                    {OTHER_CURRENCIES.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {code} - {name}
                      </option>
                    ))}
                  </optgroup>
                </Field>
              </FormGroup>
            </>
          )}
          {["qbo", "qfx", "ofx"].includes(selectedExportType) &&
            allAccounts.length > 1 && (
              <>
                {separateFilesForAccounts && (
                  <Alert variant="info" className="mt-2">
                    <>
                      <span className="mr-2">
                        {`DocuClipper has detected multiple accounts and will download
                    a zip file containing separate files for each account`}
                      </span>
                      <a
                        href="https://www.docuclipper.com/docs/docuclipper-exports-data-in-zip-file/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon="question-circle" />
                        <span className="ml-1">See instructions</span>
                        <FontAwesomeIcon
                          className="ml-2"
                          icon="external-link-alt"
                        />
                      </a>
                    </>
                  </Alert>
                )}
                <Form.Group className="mx-1 my-3" controlId="oneFilePerAccount">
                  <Form.Check
                    checked={separateFilesForAccounts}
                    onChange={(e) =>
                      setSeparateFilesForAccounts(e.target.checked)
                    }
                    type="checkbox"
                    label="Download separate files for each account"
                  />
                </Form.Group>
              </>
            )}
          <Row className="justify-content-end mx-2">
            <LoadingTrackedButton
              isLoading={loading}
              className="btn-save-qbo justify-content-center m-1"
              // disabled={anyFileUploading}
              variant="primary"
              onClick={() => {
                submitForm();
              }}
            >
              {`Download ${format2str()}`}
            </LoadingTrackedButton>
          </Row>

          {/* <FormGroup>
            <Form.Label>
              <strong>Statement Year</strong>
            </Form.Label>
            <Field name="year" type="text" component={formikInput}></Field>
          </FormGroup> */}
        </>
      )}
    </Formik>
  );
};
export default QboOptions;
